import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { clearVoucherCodeRedemptions, fetchVoucherCodeRedemptions } from '../redux/slices/vouchersSlice';
import { formatVoucherDate } from '../utils/voucherUtils';
import NoData from './NoData';

const ROWS_PER_PAGE = [25, 50, 100];

const formatMoney = (value) => `${Number(value).toFixed(2)} Lei`;

export default function VoucherCodeRedemptionsTable({ voucherId, code, onBack }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const redemptions = useSelector((state) => state.vouchers.codeRedemptions);
  const pagination = useSelector((state) => state.vouchers.codeRedemptionsPagination);
  const isLoading = useSelector((state) => state.vouchers.isCodeRedemptionsLoading);

  useEffect(() => {
    const request = dispatch(fetchVoucherCodeRedemptions({ voucherId, codeId: code.id, page: 1, limit: 25 }));
    request.unwrap().catch((error) => {
      if (error?.name !== 'AbortError') {
        toast.error(t('Voucher code redemptions could not be loaded'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }
    });

    return () => {
      request.abort();
      dispatch(clearVoucherCodeRedemptions());
    };
  }, [code.id, dispatch, t, voucherId]);

  const loadPage = (page, limit) => {
    dispatch(fetchVoucherCodeRedemptions({ voucherId, codeId: code.id, page, limit }))
      .unwrap()
      .catch(() => {
        toast.error(t('Voucher code redemptions could not be loaded'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <IconButton aria-label={t('Back to codes')} onClick={onBack}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">{code.code}</Typography>
      </Box>
      {isLoading ? (
        <Box sx={{ minHeight: 250, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      ) : redemptions.length ? (
        <Paper>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 900, '& th, & td': { whiteSpace: 'nowrap' } }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('Order ID')}</TableCell>
                  <TableCell>{t('Redeemed at')}</TableCell>
                  <TableCell>{t('Customer')}</TableCell>
                  <TableCell>{t('Email')}</TableCell>
                  <TableCell align="right">{t('Subtotal')}</TableCell>
                  <TableCell align="right">{t('Voucher discount')}</TableCell>
                  <TableCell align="right">{t('Total')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {redemptions.map((redemption) => {
                  const customerName = [redemption.customer?.firstName, redemption.customer?.lastName]
                    .filter(Boolean)
                    .join(' ');

                  return (
                    <TableRow
                      key={redemption.orderId}
                      sx={{ '&:nth-of-type(odd)': (theme) => ({ backgroundColor: theme.palette.action.hover }) }}
                    >
                      <TableCell>{redemption.orderId}</TableCell>
                      <TableCell>{formatVoucherDate(redemption.redeemedAt) || '-'}</TableCell>
                      <TableCell>{customerName || '-'}</TableCell>
                      <TableCell>{redemption.customer?.email || '-'}</TableCell>
                      <TableCell align="right">{formatMoney(redemption.subtotal)}</TableCell>
                      <TableCell align="right">-{formatMoney(redemption.voucherDiscount)}</TableCell>
                      <TableCell align="right">{formatMoney(redemption.total)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page - 1}
            onPageChange={(_, page) => loadPage(page + 1, pagination.limit)}
            rowsPerPage={pagination.limit}
            onRowsPerPageChange={(event) => loadPage(1, Number(event.target.value))}
            rowsPerPageOptions={ROWS_PER_PAGE}
            labelRowsPerPage={t('rowsPaginator')}
          />
        </Paper>
      ) : (
        <NoData />
      )}
    </Box>
  );
}
